import { fetchAuthSession } from 'aws-amplify/auth';

const APPSYNC_API_URL = import.meta.env.VITE_APPSYNC_API_URL || import.meta.env.VITE_APPSYNC_URL || '';
const APPSYNC_REALTIME_URL = import.meta.env.VITE_APPSYNC_REALTIME_URL || '';

function deriveRealtimeUrl(graphqlUrl: string): string {
  if (APPSYNC_REALTIME_URL) return APPSYNC_REALTIME_URL;
  return graphqlUrl
    .replace('https://', 'wss://')
    .replace('appsync-api', 'appsync-realtime-api');
}

const JOB_STATUS_SUBSCRIPTION = `subscription OnJobStatusUpdate($userId: ID!) {
  onJobStatusUpdate(userId: $userId) {
    jobId userId status outputLocation errorMessage timestamp progress
    currentStep currentPhase thinkingText isThinkingUpdate
    processingText isDocumentProcessingUpdate operationStatus
    markdownContent contentType isMarkdownPreview
    outputFiles { label key }
    manifestKey outputBundlePrefix
    streamingType streamingContent streamingSection
    isStreamingUpdate isStreamingChunk
    streamingChunk { chunkId jobId userId chunkIndex totalChunks content section isComplete timestamp }
  }
}`;

export interface RealtimeChannel {
  name: string;
  bind: (eventName: string, callback: Function) => void;
  unbind: (eventName?: string, callback?: Function) => void;
  unbind_all: () => void;
}

interface WsSubscription {
  id: string;
  unsubscribe: () => void;
}

const KEEPALIVE_TIMEOUT_MS = 5 * 60 * 1000;
const RECONNECT_BASE_MS = 1000;
const MAX_RECONNECT_MS = 30000;

class PusherServiceClass {
  private ws: WebSocket | null = null;
  private transportInstance: { transport: 'appsync' } | null = null;
  private isConnected: boolean = false;
  private subscribedChannels: Map<string, RealtimeChannel> = new Map();
  private eventCallbacks: Map<string, Map<string, Set<Function>>> = new Map();
  private subscriptions: Map<string, WsSubscription> = new Map();
  private connectionPromise: Promise<{ transport: 'appsync' }> | null = null;
  private keepaliveTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempts: number = 0;
  private getAuthTokenFn: (() => Promise<string | null>) | null = null;
  private pendingSubscriptions: Map<string, { userId: string; resolve: () => void }> = new Map();
  private subIdCounter: number = 0;

  private log(level: 'info' | 'warn' | 'error' | 'debug', message: string, data?: any) {
    const logMessage = `[PusherService] ${message}`;
    if (data) {
      console[level](logMessage, data);
    } else {
      console[level](logMessage);
    }
  }

  private async getJwtToken(): Promise<string> {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      if (!token) throw new Error('No idToken in session');
      return token;
    } catch (err) {
      if (this.getAuthTokenFn) {
        const fallback = await this.getAuthTokenFn();
        if (fallback) return fallback;
      }
      throw new Error('Unable to obtain JWT token for AppSync');
    }
  }

  async initialize(getAuthToken: () => Promise<string | null>): Promise<{ transport: 'appsync' }> {
    this.getAuthTokenFn = getAuthToken;

    if (this.transportInstance && this.isConnected && this.ws?.readyState === WebSocket.OPEN) {
      return this.transportInstance;
    }

    if (this.connectionPromise) {
      return this.connectionPromise;
    }

    if (!APPSYNC_API_URL) {
      throw new Error('AppSync environment variables not configured');
    }

    this.connectionPromise = this.createConnection();
    return this.connectionPromise;
  }

  private async createConnection(): Promise<{ transport: 'appsync' }> {
    try {
      if (this.ws) {
        this.log('info', 'Closing existing WebSocket before reconnect');
        this.ws.onclose = null;
        this.ws.onerror = null;
        this.ws.onmessage = null;
        this.ws.close();
        this.ws = null;
      }

      const token = await this.getJwtToken();
      const host = new URL(APPSYNC_API_URL).host;

      const header = btoa(JSON.stringify({
        host,
        Authorization: token,
      }));
      const payload = btoa('{}');

      const realtimeUrl = deriveRealtimeUrl(APPSYNC_API_URL);
      const url = `${realtimeUrl}?header=${encodeURIComponent(header)}&payload=${encodeURIComponent(payload)}`;

      this.log('info', 'Connecting to AppSync realtime', { realtimeUrl: realtimeUrl.substring(0, 60) + '...' });

      return new Promise<{ transport: 'appsync' }>((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('AppSync WebSocket connection timeout (10s)'));
          this.ws?.close();
        }, 10000);

        this.ws = new WebSocket(url, ['graphql-ws']);

        this.ws.onopen = () => {
          this.log('info', 'WebSocket opened, sending connection_init');
          this.ws!.send(JSON.stringify({ type: 'connection_init' }));
        };

        this.ws.onmessage = (event) => {
          let msg: any;
          try { msg = JSON.parse(event.data); } catch { return; }

          switch (msg.type) {
            case 'connection_ack': {
              clearTimeout(timeout);
              const timeoutMs = msg.payload?.connectionTimeoutMs || KEEPALIVE_TIMEOUT_MS;
              this.resetKeepalive(timeoutMs);
              this.transportInstance = { transport: 'appsync' };
              this.isConnected = true;
              this.connectionPromise = null;
              this.reconnectAttempts = 0;
              this.log('info', 'AppSync realtime connected (connection_ack)');
              resolve(this.transportInstance);

              this.resubscribeAll();
              break;
            }
            case 'start_ack': {
              this.log('info', 'AppSync subscription acknowledged', { id: msg.id });
              break;
            }
            case 'ka':
              this.resetKeepalive(msg.payload?.connectionTimeoutMs || KEEPALIVE_TIMEOUT_MS);
              break;

            case 'data': {
              const subId = msg.id;
              const update = msg.payload?.data?.onJobStatusUpdate;
              if (!update || !subId) {
                this.log('warn', 'Received data message with missing update or subId', { hasUpdate: !!update, subId });
                break;
              }

              let dispatched = false;
              this.subscriptions.forEach((sub, channelName) => {
                if (sub.id === subId) {
                  const callbacks = this.eventCallbacks.get(channelName)?.get('job-status-update');
                  const callbackCount = callbacks?.size ?? 0;
                  this.log('debug', `Dispatching update to ${callbackCount} callbacks`, {
                    jobId: update.jobId,
                    status: update.status,
                    isStreamingUpdate: update.isStreamingUpdate,
                    hasStreamingContent: !!update.streamingContent,
                    currentStep: update.currentStep,
                    channelName,
                    subId,
                  });
                  callbacks?.forEach((cb) => cb(update));
                  dispatched = true;
                }
              });
              if (!dispatched) {
                this.log('warn', 'No matching subscription for data message', {
                  subId,
                  jobId: update.jobId,
                  registeredSubIds: Array.from(this.subscriptions.entries()).map(([ch, s]) => ({ channel: ch, id: s.id })),
                });
              }
              break;
            }
            case 'error': {
              this.log('error', 'AppSync subscription error message', msg.payload);
              if (!this.isConnected) {
                clearTimeout(timeout);
                reject(new Error(JSON.stringify(msg.payload)));
              }
              break;
            }
            case 'connection_error': {
              this.log('error', 'AppSync connection error message', msg.payload);
              clearTimeout(timeout);
              reject(new Error(JSON.stringify(msg.payload)));
              break;
            }
            case 'complete': {
              this.log('warn', 'AppSync subscription completed', { id: msg.id });
              break;
            }
            default:
              this.log('debug', `Unhandled WS message type: ${msg.type}`);
          }
        };

        this.ws.onerror = (err) => {
          this.log('error', 'WebSocket error', err);
          this.isConnected = false;
        };

        this.ws.onclose = (event) => {
          this.log('warn', `WebSocket closed (code=${event.code}, reason=${event.reason})`);
          this.isConnected = false;
          this.transportInstance = null;
          this.connectionPromise = null;
          this.clearKeepalive();

          if (this.subscribedChannels.size > 0) {
            this.scheduleReconnect();
          }
        };
      });
    } catch (error) {
      this.log('error', 'Failed to create AppSync connection', error);
      this.transportInstance = null;
      this.isConnected = false;
      this.connectionPromise = null;
      throw error;
    }
  }

  private resetKeepalive(timeoutMs: number) {
    this.clearKeepalive();
    this.keepaliveTimer = setTimeout(() => {
      this.log('warn', 'Keepalive timeout, reconnecting');
      this.ws?.close();
    }, timeoutMs + 5000);
  }

  private clearKeepalive() {
    if (this.keepaliveTimer) {
      clearTimeout(this.keepaliveTimer);
      this.keepaliveTimer = null;
    }
  }

  private scheduleReconnect() {
    const delay = Math.min(RECONNECT_BASE_MS * Math.pow(2, this.reconnectAttempts), MAX_RECONNECT_MS);
    this.reconnectAttempts++;
    this.log('info', `Scheduling reconnect in ${delay}ms (attempt ${this.reconnectAttempts})`);

    setTimeout(async () => {
      if (this.subscribedChannels.size === 0) return;
      try {
        this.connectionPromise = this.createConnection();
        await this.connectionPromise;
      } catch (err) {
        this.log('error', 'Reconnect failed', err);
      }
    }, delay);
  }

  private resubscribeAll() {
    this.subscribedChannels.forEach((_channel, channelName) => {
      const userId = channelName.replace(/^private-job-/, '');
      this.registerGraphqlSubscription(channelName, userId);
    });
  }

  private registerGraphqlSubscription(channelName: string, userId: string) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;

    const existing = this.subscriptions.get(channelName);
    if (existing) {
      try { this.ws.send(JSON.stringify({ type: 'stop', id: existing.id })); } catch {}
    }

    const subId = String(++this.subIdCounter);

    const registerMsg = {
      id: subId,
      type: 'start',
      payload: {
        data: JSON.stringify({
          query: JOB_STATUS_SUBSCRIPTION,
          variables: { userId },
        }),
        extensions: {
          authorization: {
            host: new URL(APPSYNC_API_URL).host,
            Authorization: this.getAuthTokenFn
              ? 'pending'
              : '',
          },
        },
      },
    };

    this.getJwtToken()
      .then((token) => {
        registerMsg.payload.extensions.authorization.Authorization = token;
        this.ws?.send(JSON.stringify(registerMsg));
        this.log('info', `Registered subscription ${subId} for userId=${userId}`);
      })
      .catch((err) => {
        this.log('error', 'Failed to get token for subscription registration', err);
      });

    this.subscriptions.set(channelName, {
      id: subId,
      unsubscribe: () => {
        if (this.ws?.readyState === WebSocket.OPEN) {
          try { this.ws.send(JSON.stringify({ type: 'stop', id: subId })); } catch {}
        }
      },
    });
  }

  async subscribe(channelName: string, getAuthToken: () => Promise<string | null>): Promise<RealtimeChannel | null> {
    try {
      await this.initialize(getAuthToken);

      if (this.subscribedChannels.has(channelName)) {
        return this.subscribedChannels.get(channelName)!;
      }

      const channel: RealtimeChannel = {
        name: channelName,
        bind: (eventName: string, callback: Function) => {
          const channelCallbacks = this.eventCallbacks.get(channelName);
          if (!channelCallbacks) return;
          if (!channelCallbacks.has(eventName)) channelCallbacks.set(eventName, new Set());
          channelCallbacks.get(eventName)!.add(callback);
        },
        unbind: (eventName?: string, callback?: Function) => {
          const channelCallbacks = this.eventCallbacks.get(channelName);
          if (!channelCallbacks) return;
          if (!eventName) { channelCallbacks.clear(); return; }
          if (!channelCallbacks.has(eventName)) return;
          if (callback) { channelCallbacks.get(eventName)!.delete(callback); }
          else { channelCallbacks.delete(eventName); }
        },
        unbind_all: () => {
          this.eventCallbacks.get(channelName)?.clear();
        },
      };

      this.subscribedChannels.set(channelName, channel);
      if (!this.eventCallbacks.has(channelName)) {
        this.eventCallbacks.set(channelName, new Map());
      }

      return channel;
    } catch (error) {
      this.log('error', `Failed to subscribe to channel ${channelName}`, error);
      return null;
    }
  }

  unsubscribe(channelName: string): void {
    if (this.subscribedChannels.has(channelName)) {
      this.log('info', `Unsubscribing from channel: ${channelName}`);
      this.subscriptions.get(channelName)?.unsubscribe();
      this.subscriptions.delete(channelName);
      this.subscribedChannels.delete(channelName);
      this.eventCallbacks.delete(channelName);
    }
  }

  async bind<T = any>(
    channelName: string,
    eventName: string,
    callback: (data: T) => void,
    getAuthToken: () => Promise<string | null>
  ): Promise<void> {
    const channel = await this.subscribe(channelName, getAuthToken);
    if (!channel) {
      this.log('error', `Cannot bind to event '${eventName}': channel subscription failed`);
      return;
    }

    const channelCallbacks = this.eventCallbacks.get(channelName)!;
    if (!channelCallbacks.has(eventName)) channelCallbacks.set(eventName, new Set());
    channelCallbacks.get(eventName)!.add(callback);

    if (!this.subscriptions.has(channelName)) {
      const userId = channelName.replace(/^private-job-/, '');
      this.registerGraphqlSubscription(channelName, userId);
    }

    this.log('debug', `Bound to event '${eventName}' on channel '${channelName}'`);
  }

  unbind(channelName: string, eventName: string, callback?: Function): void {
    if (!this.subscribedChannels.has(channelName)) return;

    if (callback) {
      this.eventCallbacks.get(channelName)?.get(eventName)?.delete(callback);
    } else {
      this.eventCallbacks.get(channelName)?.delete(eventName);
    }

    const channelCallbacks = this.eventCallbacks.get(channelName);
    const hasCallbacks = !!channelCallbacks && Array.from(channelCallbacks.values()).some((s) => s.size > 0);
    if (!hasCallbacks) {
      this.unsubscribe(channelName);
    }
  }

  getConnectionStatus(): { isConnected: boolean; pusher: { transport: 'appsync' } | null } {
    return {
      isConnected: this.isConnected,
      pusher: this.transportInstance,
    };
  }

  async reconnect(getAuthToken: () => Promise<string | null>): Promise<void> {
    this.log('info', 'Forcing reconnection...');
    this.cleanup();
    await this.initialize(getAuthToken);
  }

  cleanup(): void {
    this.log('info', 'Cleaning up AppSync service');

    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.clearKeepalive();

    if (this.ws) {
      this.ws.onclose = null;
      this.ws.onerror = null;
      this.ws.onmessage = null;
      this.ws.close();
      this.ws = null;
    }

    this.transportInstance = null;
    this.isConnected = false;
    this.subscribedChannels.clear();
    this.eventCallbacks.clear();
    this.subscriptions.clear();
    this.connectionPromise = null;
    this.pendingSubscriptions.clear();
  }

  getActiveChannelCount(): number {
    return this.subscribedChannels.size;
  }

  getDebugInfo() {
    return {
      isConnected: this.isConnected,
      activeChannels: Array.from(this.subscribedChannels.keys()),
      channelCount: this.subscribedChannels.size,
      hasInstance: !!this.transportInstance,
      hasConnectionPromise: !!this.connectionPromise,
      wsState: this.ws ? ['CONNECTING', 'OPEN', 'CLOSING', 'CLOSED'][this.ws.readyState] : 'NONE',
    };
  }
}

export const PusherService = new PusherServiceClass();
export default PusherService;

if (typeof window !== 'undefined') {
  (window as any).__PusherDebug = () => {
    const info = PusherService.getDebugInfo();
    const subs = (PusherService as any).subscriptions as Map<string, any>;
    const cbs = (PusherService as any).eventCallbacks as Map<string, Map<string, Set<Function>>>;
    const callbackCounts: Record<string, number> = {};
    cbs.forEach((events, ch) => {
      events.forEach((set, ev) => {
        callbackCounts[`${ch}/${ev}`] = set.size;
      });
    });
    console.table({
      ...info,
      subscriptionIds: Array.from(subs.entries()).map(([ch, s]) => `${ch} → ${s.id}`),
      callbackCounts,
    });
    return { ...info, callbackCounts };
  };
}
